import { useEffect, useMemo, useState } from 'react';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import { cn } from '@Utils/cn';
import {
  FilterClause,
  OmniSearchBody,
  useOmniSearch,
  useSearchSchema,
} from '@Hooks/useOmniSearch';
import { OmniFilterBar } from './OmniFilterBar';
import { OmniResultsTable, SortState } from './OmniResultsTable';
import { EntityInspector, SelectedEntity } from './EntityInspector';

const PAGE_SIZE = 20;

function formatCount(n: number | null, capped: boolean): string {
  if (n == null) return '—';
  if (capped) return `${n.toLocaleString()}+`;
  return n.toLocaleString();
}

/** Parse a Meili sort expression ('field:desc') into table sort state. */
function parseSort(expr?: string | null): SortState {
  if (!expr) return null;
  const [field, dir] = expr.split(':');
  return { field, dir: dir === 'asc' ? 'asc' : 'desc' };
}

export default function OmniSearch() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState('activity');
  const [filtersByIndex, setFiltersByIndex] = useState<Record<string, FilterClause[]>>({});
  const [sortByIndex, setSortByIndex] = useState<Record<string, string>>({});
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<SelectedEntity | null>(null);

  // Debounce the search box into the query that actually fires requests.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 350);
    return () => clearTimeout(t);
  }, [query]);

  // Reset pagination whenever the population being viewed changes.
  useEffect(() => {
    setPage(0);
  }, [debouncedQuery, activeIndex, filtersByIndex, sortByIndex]);

  // Tab counts: one cross-index query (global text only, no per-index filters).
  const countsBody = useMemo<OmniSearchBody>(
    () => ({ q: debouncedQuery, limit: 1, countUniqueFish: false }),
    [debouncedQuery],
  );
  const { data: counts } = useOmniSearch(countsBody);
  // Surface Activity first; keep the registry order for the rest.
  const tabs = useMemo(() => {
    const results = counts?.results ?? [];
    return [...results].sort((a, b) =>
      a.index === 'activity' ? -1 : b.index === 'activity' ? 1 : 0,
    );
  }, [counts]);

  const { data: schemaData } = useSearchSchema(activeIndex);
  const schema = schemaData?.data;

  // Active tab: a scoped query with that index's filters, sort + pagination. Only an explicit
  // user sort is sent; absent that, the backend applies the index's configured default sort.
  const activeFilters = filtersByIndex[activeIndex] ?? [];
  const userSort = sortByIndex[activeIndex];
  const activeBody = useMemo<OmniSearchBody>(
    () => ({
      q: debouncedQuery,
      indexes: [activeIndex],
      perIndex: { [activeIndex]: { filters: activeFilters, sort: userSort ? [userSort] : undefined } },
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [debouncedQuery, activeIndex, activeFilters, userSort, page],
  );
  const { data: activeData, status } = useOmniSearch(activeBody);
  const active = activeData?.results?.[0];

  // What the table highlights: the user's sort, else the index's configured default.
  const effectiveSort = parseSort(userSort ?? schema?.defaultSort);

  const handleSort = (field: string) => {
    const current = parseSort(userSort ?? schema?.defaultSort);
    const dir = current?.field === field && current.dir === 'desc' ? 'asc' : 'desc';
    setSortByIndex((prev) => ({ ...prev, [activeIndex]: `${field}:${dir}` }));
  };

  const total = active?.totalHits ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-bold">Omni Search</h1>
        <p className="text-sm text-zinc-500">
          Search and explore every index. Filter a population, see who they are, and click any row to
          inspect it.
        </p>
      </div>

      <Input
        placeholder="Search across members, fish, reservations, events, activity…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="w-full"
      />

      {/* Index tabs with counts */}
      <div className="flex flex-wrap gap-2 border-b border-zinc-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.index}
            onClick={() => setActiveIndex(t.index)}
            className={cn(
              'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium',
              activeIndex === t.index ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-100',
            )}
          >
            {t.label}
            <Badge variant={activeIndex === t.index ? 'secondary' : 'outline'}>
              {formatCount(t.totalHits, false)}
            </Badge>
          </button>
        ))}
      </div>

      {/* Filters */}
      {schema && (
        <OmniFilterBar
          index={activeIndex}
          fields={schema.fields}
          value={activeFilters}
          onChange={(next) => setFiltersByIndex((prev) => ({ ...prev, [activeIndex]: next }))}
        />
      )}

      {/* Stats bar */}
      <div className="flex items-center gap-4 rounded-lg bg-zinc-100 px-4 py-2 text-sm">
        <span>
          <span className="font-bold">{formatCount(total, false)}</span> results
        </span>
        {active?.uniqueFishCount != null && (
          <span>
            <span className="font-bold">
              {formatCount(active.uniqueFishCount, active.uniqueFishCapped)}
            </span>{' '}
            unique fish
          </span>
        )}
      </div>

      {/* Results */}
      <OmniResultsTable
        schema={schema}
        hits={active?.hits ?? []}
        status={status === 'success' && !active ? 'idle' : status}
        pageSize={PAGE_SIZE}
        sort={effectiveSort}
        onSort={handleSort}
        onRowClick={(hit) =>
          setSelected({ index: activeIndex, label: active?.label ?? activeIndex, hit })
        }
      />

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-end gap-3 text-sm">
          <span className="text-zinc-500">
            Page {page + 1} of {pageCount}
          </span>
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page + 1 >= pageCount}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      <EntityInspector selected={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
